import React, { forwardRef, useId } from "react";
import classNames from "classnames";
import "./Textarea.scss";

/** Пропсы {@link Textarea}. */
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: string;
    required?: boolean;
    /** Флаг ошибки (подсветка control). Текст ошибки — в `FormField` / `BusinessForm.Field`. */
    error?: boolean;
    description?: string;
    className?: string;
    textareaClassName?: string;
}

/**
 * Многострочное поле shared UI с тем же контрактом, что {@link Input}.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
    {
        label,
        required = false,
        error,
        description,
        className,
        textareaClassName,
        disabled,
        readOnly,
        id: idProp,
        rows = 3,
        ...textareaProps
    },
    ref,
) {
    const generatedId = useId();
    const inputId = idProp ?? generatedId;
    const hasError = error === true;
    const describedBy = description != null && description.length > 0 ? `${inputId}-desc` : undefined;

    return (
        <div
            className={classNames("shared_textarea", className, {
                shared_textarea__disabled: disabled,
                shared_textarea__error: hasError,
            })}
        >
            {label != null && label.length > 0 && (
                <label htmlFor={inputId} className="shared_textarea__label">
                    {label}
                    {required && (
                        <span className="shared_textarea__required" aria-hidden="true">
                            *
                        </span>
                    )}
                </label>
            )}
            <textarea
                ref={ref}
                id={inputId}
                rows={rows}
                disabled={disabled}
                readOnly={readOnly}
                aria-invalid={hasError ? true : undefined}
                aria-describedby={describedBy}
                className={classNames("shared_textarea__field", "custom_scrollbar", textareaClassName)}
                {...textareaProps}
            />
            {description != null && description.length > 0 && (
                <p id={`${inputId}-desc`} className="shared_textarea__description">
                    {description}
                </p>
            )}
        </div>
    );
});

Textarea.displayName = "Textarea";
